import { normalizeApiError as normalizeTransportApiError, type NormalizedApiError } from "./cmtsHttpClient.js";
import { normalizeCmtsApiError as normalizeDomainApiError, type CmtsApiError as DomainCmtsApiError } from "../errors/index.js";

export interface UserSafeCmtsApiError {
  readonly userMessage: string;
  readonly technicalMessage?: string | undefined;
  readonly status?: number | undefined;
  readonly code?: string | undefined;
  readonly correlationId?: string | undefined;
  readonly fieldErrors?: Record<string, string[]> | undefined;
  readonly retryable: boolean;
  readonly authExpired?: boolean | undefined;
  readonly permissionDenied?: boolean | undefined;
  readonly raw?: unknown | undefined;
}

export function normalizeUserSafeApiError(error: unknown): UserSafeCmtsApiError {
  const domain = normalizeDomainApiError(error);
  const transport = safeTransportError(error);
  const userMessage = redactTechnicalText(domain.message || transport?.message || "The CMTS request could not be completed.");
  return {
    userMessage,
    technicalMessage: domain.technicalDetails,
    status: domain.httpStatus ?? transport?.httpStatus,
    code: domain.code ?? transport?.errorCode,
    correlationId: domain.correlationId ?? transport?.correlationId,
    fieldErrors: Object.keys(domain.fieldErrors).length ? domain.fieldErrors : toFieldErrorRecord(transport?.fieldErrors),
    retryable: domain.retryable || Boolean(transport?.isNetworkError || transport?.isTimeout),
    authExpired: (domain.httpStatus ?? transport?.httpStatus) === 401,
    permissionDenied: domain.category === "authorization" || (domain.httpStatus ?? transport?.httpStatus) === 403,
    raw: error,
  };
}

export function normalizeApiErrorForUi(error: unknown): DomainCmtsApiError {
  const normalized = normalizeDomainApiError(error);
  return {
    ...normalized,
    message: redactTechnicalText(normalized.message),
    technicalDetails: normalized.technicalDetails ? redactTechnicalText(normalized.technicalDetails) : undefined,
  };
}

export function redactTechnicalText(message: string): string {
  if (!message.trim()) return "The CMTS request could not be completed.";

  const technicalPattern = /\bat\s+\S+\.(?:tsx?|jsx?|cs):\d+(?::\d+)?|stack trace|node_modules|webpack|vite|System\.[A-Za-z]+Exception|Microsoft\.|Exception:|Npgsql|SqlException|PostgresException|EntityFrameworkCore|\bSELECT\s+|\bINSERT\s+|\bUPDATE\s+|\bDELETE\s+|foreign key|duplicate key|constraint failed|violates .* constraint/i;

  if (technicalPattern.test(message)) {
    return "The CMTS request could not be completed.";
  }

  return message;
}

function safeTransportError(error: unknown): NormalizedApiError | undefined {
  try {
    return normalizeTransportApiError(error);
  } catch {
    return undefined;
  }
}

function toFieldErrorRecord(errors: readonly { readonly field: string; readonly message: string }[] | undefined): Record<string, string[]> | undefined {
  if (!errors?.length) return undefined;
  const result: Record<string, string[]> = {};
  for (const item of errors) {
    if (!item.field) continue;
    result[item.field] = [...(result[item.field] ?? []), item.message];
  }
  return Object.keys(result).length ? result : undefined;
}
